import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

interface MissedCycle {
  date: string;
  amount: number;
  currency: string;
}

interface CatchUpBillingWizardProps {
  open: boolean;
  onClose: () => void;
  subscriptionName: string;
  missedCycles: MissedCycle[];
  truncated: boolean;
  onConfirm: (confirmedCycles: { date: string; amount: number }[]) => Promise<void>;
}

export function CatchUpBillingWizard({ open, onClose, subscriptionName, missedCycles, truncated, onConfirm }: CatchUpBillingWizardProps) {
  const [selected, setSelected] = useState<Set<number>>(() => new Set(missedCycles.map((_, i) => i)));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set(missedCycles.map((_, i) => i)));
  }, [open, missedCycles]);

  const toggleAll = (checked: boolean | 'indeterminate') => {
    setSelected(checked === true ? new Set(missedCycles.map((_, i) => i)) : new Set());
  };

  const toggleOne = (index: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const cycles = missedCycles
        .filter((_, i) => selected.has(i))
        .map(c => ({ date: c.date, amount: c.amount }));
      await onConfirm(cycles);
    } finally {
      setLoading(false);
    }
  };

  const handleSkipAll = async () => {
    setLoading(true);
    try {
      await onConfirm([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="catchup-wizard-dialog" className="max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Catch Up on Missed Billing</DialogTitle>
          <DialogDescription>
            {missedCycles.length} billing cycle{missedCycles.length > 1 ? 's were' : ' was'} missed while <strong>{subscriptionName}</strong> was paused. Select which ones to record as transactions.
          </DialogDescription>
        </DialogHeader>

        {truncated && (
          <div className="flex items-center gap-2 text-sm text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-950/30 rounded-md p-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Showing first 24 of more missed cycles
          </div>
        )}

        <div className="space-y-1">
          <div className="flex items-center gap-2 py-2 border-b">
            <Checkbox
              checked={selected.size === missedCycles.length}
              onCheckedChange={toggleAll}
              data-testid="catchup-select-all"
            />
            <span className="text-sm font-medium">Select All</span>
            <Badge variant="secondary" className="ml-auto">{selected.size}/{missedCycles.length}</Badge>
          </div>
          {missedCycles.map((cycle, i) => (
            <label key={i} className="flex items-center gap-2 py-1.5 cursor-pointer hover:bg-muted/50 rounded px-1">
              <Checkbox
                checked={selected.has(i)}
                onCheckedChange={() => toggleOne(i)}
                data-testid={`catchup-cycle-checkbox-${i}`}
              />
              <span className="text-sm" data-testid={`catchup-cycle-date-${i}`}>
                {new Date(cycle.date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
              </span>
              <span className="ml-auto text-sm font-mono">
                {formatCurrency(cycle.amount, cycle.currency)}
              </span>
            </label>
          ))}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="ghost" onClick={handleSkipAll} disabled={loading} data-testid="catchup-skip-all-btn" className="sm:mr-auto">
            Skip All &amp; Resume
          </Button>
          <Button variant="outline" onClick={onClose} disabled={loading} data-testid="catchup-cancel-btn">
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={loading || selected.size === 0} data-testid="catchup-confirm-btn">
            Confirm Selected ({selected.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
